import ShiptAnalytics, { AnalyticsEvent, type FirstOrderPlaced } from '@shipt/analytics-member-web'
import { track } from '@/features/shared/analytics/trackingQueue'
import { getUser } from '@/features/account/services/User/utils'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { isOnServer } from '@shared/constants/util'
import {
  getAllUserTraits,
  shouldDisableAnalytics,
  urlPathnameToLocationName,
  getCPATraits,
  USER_TRAITS_SESSION_STORAGE,
} from '@/features/shared/analytics/utils'
import { type ErrorMessage } from '@/features/shared/utils/dataFetching/types'
import { type User } from '@/features/account/services/User/types'
import { userQueryOptions } from '@/features/account/services/User/queryOptions'
import { getConsentObject, getIsConsentedToAnalytics, waitForAirgap } from '@/lib/transcend/utils'
import { type Consent } from '@shipt/analytics-core'
import { setSessionStorageItem } from '@/features/shared/utils/sessionStorage'

export type ConsentContext = {
  context: {
    consent: Consent | undefined
  }
}

export const trackRegistrationCompleted = (authSource: string | null) =>
  track({
    eventName: AnalyticsEvent.RegistrationCompleted,
    properties: {
      auth_source: authSource ?? '',
      location: urlPathnameToLocationName(),
    },
  })

export const trackLoginCompleted = (authSource: string | null, isTarget360: boolean) =>
  track({
    eventName: AnalyticsEvent.LoginCompleted,
    properties: {
      auth_source: authSource ?? '',
      is_c360_active_or_trialing: isTarget360,
    },
  })

export const trackUserLoginError = (error: ErrorMessage | null) =>
  track({
    eventName: AnalyticsEvent.LoginError,
    properties: { error_context: error?.message || '' },
  })

export const identifyUser = (user: User) => {
  if (isOnServer() || shouldDisableAnalytics()) return
  const userTraits = { ...getAllUserTraits(user), ...getCPATraits() }

  if (getIsConsentedToAnalytics()) {
    const consentContext: ConsentContext = {
      context: {
        consent: getConsentObject(),
      },
    }
    ShiptAnalytics.identify(userTraits, consentContext)
  } else {
    waitForAirgap((consent) => {
      ShiptAnalytics.identify(userTraits, { context: { consent } })
    })
  }
  // User traits are persisted client-side for ad platform optimization and
  // reporting, which is retrieved by GTM.
  setSessionStorageItem(USER_TRAITS_SESSION_STORAGE, JSON.stringify(userTraits))
}

export const trackUserFirstOrderPlaced = (eventProperties: FirstOrderPlaced['properties']) => {
  const user = getUser(getQueryClient())
  if (!user) return

  const userHasPlacedFirstOrder = Boolean(user.has_placed_first_order)
  if (!userHasPlacedFirstOrder) {
    track({
      eventName: AnalyticsEvent.FirstOrderPlaced,
      properties: eventProperties,
      eventOptions: {
        sendImmediately: true,
      },
    })
    getQueryClient().setQueryData(userQueryOptions.queryKey, {
      ...user,
      has_placed_first_order: true,
      has_placed_marketplace_order: true,
    })
  }
}
